// STEP 1, `/details` — THE APP'S "CONFIRM YOUR DETAILS" (SubjectPropertyView), WEB-PARITY-1C AND 2A.
//
// Tyler, 2026-09-29: "Property details should have the image of the home.. fields should be editable (such as the home
// type, etc). It should model the app perfectly". So this is the app's step 1, card for card: the hero (DT-1), the
// satellite view (DT-2), Identification with the parcel editable until committed (DT-7, DT-8), Value with the assessed
// value editable in place and its year (DT-9, DT-10), the ratio card on the app's rule (DT-13, DT-14), Characteristics
// with the app's pickers and fields and the correction lines (DT-16 to DT-22), and the requirement sentence, Continue and
// the app's alerts (DT-24, DT-25). The "Correct the record" panel (DT-26) and the implied market value row (DT-11) are
// gone. Every word is the app's (`detailsWording.ts`, each pinned to its line) or the server's (`Copy`, with its code).
//
// WEB-PARITY-2A ADDS THE REST OF THE APP'S STEP 1, in its order (SubjectPropertyView.swift:474-521): the record the owner
// is asked about — a near miss, or a building's parcels (DT-29, DT-30) — the refusal, the records service we could not
// reach, the building-scale record, "Enter these from your assessment notice" and the partial-records note (DT-28), the
// recent purchase with the app's routes (DT-31, DT-32), the tax year (DT-12), the ratio from the owner's notice with its
// status line (DT-15), the Jurisdiction card with the owner's own deadline (DT-23), and the low figure said beside the
// figure rather than at the top of the page (DT-33). Those cards live in `detailsCards.tsx`.
//
// WHAT IS STILL THE SERVER'S. Every figure is `v2ResolveProperty` `record`'s subject, and every judgement — is the record
// the owner's (`SubjectView.record`), is the assessed value believable (P-1, NEW-2, and P-1's diagnosis of why not), is the
// size there (NEW-3), is it complete (G-4), does a purchase rule out price (NEW-7) — is its decision. The app judges these
// on the phone; here the page asks the server again after each edit and shows what it answers. The app's alerts are how
// those answers stop the owner at Continue, in the app's order (`attemptContinue`, SubjectPropertyView.swift:675-721).
//
// EVERY EDIT SENDS THE WHOLE SET (F-WP1A-1, `app/corrections.ts`) — the owner's corrections AND their answers about the
// record: a `record` call's set replaces the last call's, a key it omits is the county's figure again, and an answer it
// omits is no answer, which adopts nothing. So each edit is laid over everything the owner has said, and the calls go
// one after another, so the set the server holds last is the set the owner made last.

import { useEffect, useRef, useState, type RefObject } from "react";
import { useNavigate } from "react-router-dom";
import type { PropertyTypeCode, ResolvePropertyResponse, SubjectView, V2Failure } from "@contract";
import { useAppealId, useSession } from "../../app/session";
import {
  answersOn, correctionsOn, recordInput, recordKeepingCorrections, withAnswers, withCorrection, type Corrections,
  type RecordAnswers,
} from "../../app/corrections";
import { CopyNote, DecisionView, DegradedList, HeldLine } from "../../components/copy";
import { heldPurchaseAnswer, keepsNotOpenMarket } from "../../app/purchaseNotice";
import { PaidRoute } from "../../components/PaidRoute";
import { Refusal } from "../../components/Refusal";
import { SatelliteImage } from "../../components/SatelliteImage";
import { AppAlert } from "../../components/AppAlert";
import { money } from "../../format";
import { words, type DetailsWordingId } from "./detailsWording";
import {
  RECORD_REFUSALS, SHOWN_BELOW, bathroomLabel, bathroomOptions, bedroomLabel, bedroomOptions, blockingDecisions,
  commitAssessed, commitNumber, commitText, diagnosisOf, lowValueLine, ownerEnteredOf, p1Prompt, partialRecords, ranBlock,
  ranWarn, refusalExplanation, requirementSentence, sqftCorrection, standingOf, unitConfirmation, unitGaps, type Correct,
  type Standing,
} from "./detailsRules";
import { BrandCard, LockedRow, NumberField, SelectField, SizeWords, TextField, Words, YearStepper } from "./detailsFields";
import {
  BuildingCard, JurisdictionCard, OwnerSuppliedCard, PartialRecordsNote, PurchaseCard, RatioCard, RecordQuestionCard,
  RefusalCard, UnreachableCard,
} from "./detailsCards";

/** The records service could not be reached: the server's 503, which wrote nothing (`resolve/record.ts`). */
function recordsUnreachable(failure: V2Failure): boolean {
  return failure.kind === "v2Error" && failure.error.error.code === "PROVIDERS_UNAVAILABLE";
}

export function Details() {
  const session = useSession();
  const appealId = useAppealId();
  // "Enter details from my notice" after an outage (DT-28): the next call asks nobody, and the subject it answers with
  // carries the choice, so every call after it resends it (`answersOn`).
  const [withoutLookup, setWithoutLookup] = useState(false);
  const [tries, setTries] = useState(0);
  return (
    <PaidRoute<ResolvePropertyResponse>
      probe={() => recordKeepingCorrections(session.transport, appealId, withoutLookup ? { withoutLookup: true } : {})}
      deps={[session.transport, appealId, session.revision, withoutLookup, tries]}
      pending="loading.record"
      failed={(failure) =>
        recordsUnreachable(failure) ? (
          <div className="details-step">
            <Hero />
            <UnreachableCard onRetry={() => setTries((n) => n + 1)} onManual={() => setWithoutLookup(true)} busy={false} />
          </div>
        ) : undefined
      }
    >
      {(response) => <DetailsStep key={response.requestId} first={response} />}
    </PaidRoute>
  );
}

type Alert = "p1" | "new2" | "new3" | "new7";
type Candidate = Standing["unitCandidates"][number];

/** A decision's server sentence code, when it ran and blocked with one. */
function codeOf(decision: { ran: boolean; outcome?: string; copy?: { code: string } | null }): string | undefined {
  return decision.ran && decision.outcome === "block" ? decision.copy?.code : undefined;
}

/** Today as a calendar day, in the owner's own zone: the day the app's `Date()` reads. */
function todayIso(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function DetailsStep({ first }: { first: ResolvePropertyResponse }) {
  const session = useSession();
  const appealId = useAppealId();
  const navigate = useNavigate();

  const [response, setResponse] = useState(first);
  const latest = useRef(first);
  const [failure, setFailure] = useState<V2Failure | null>(null);
  const [degraded, setDegraded] = useState<readonly { field: string; problem: string }[]>(first.degraded);
  const held = useRef<Corrections>(correctionsOn(first.body.subject));
  const heldAnswers = useRef<RecordAnswers>(answersOn(first.body.subject));
  const chain = useRef<Promise<void>>(Promise.resolve());
  const sent = useRef(0);
  const lastFailed = useRef(false);

  const [parcelCommitted, setParcelCommitted] = useState(false);
  const [valueSkipped, setValueSkipped] = useState(false);
  // LAUNCH-PF: the owner's answer to the purchase notice BEFORE the spend, carried in (`app/purchaseNotice.ts`). "My home
  // has a condition or record problem" opens this step with the card's grounds route already chosen — the app's
  // `recentPurchaseAckedForGrounds`, which its card shows and which does not block Continue.
  const carried = useRef(heldPurchaseAnswer(appealId));
  const [groundsChosen, setGroundsChosen] = useState(carried.current?.answer === "otherGrounds");
  const [alert, setAlert] = useState<Alert | null>(null);
  // "Enter details from your assessment notice" on the refusal card (`enterOwnerSuppliedDetails`, :2904-2909).
  const [refusalDismissed, setRefusalDismissed] = useState(false);
  // The parcel the owner tapped, held for "File against this parcel?" rather than sent on the tap (:20-25).
  const [pendingPick, setPendingPick] = useState<Candidate | null>(null);

  const valueInput = useRef<HTMLInputElement>(null);
  const sqftInput = useRef<HTMLInputElement>(null);
  const purchaseCard = useRef<HTMLDivElement>(null);
  const notes = useRef<HTMLDivElement>(null);

  /** Everything the owner has said, sent after any call still on its way. */
  function send(): void {
    const set = held.current;
    const answers = heldAnswers.current;
    const mine = ++sent.current;
    chain.current = chain.current.then(async () => {
      const result = await session.transport.v2ResolveProperty.record(recordInput(appealId, set, answers));
      if (mine !== sent.current) return;
      lastFailed.current = !result.ok;
      if (result.ok) {
        latest.current = result.response;
        setResponse(result.response);
        setFailure(null);
        setDegraded(result.response.degraded);
      } else {
        setFailure(result);
      }
    });
  }

  /** One correction, laid over every other the owner has made. */
  const correct: Correct = (key, value) => {
    held.current = withCorrection(held.current, key, value);
    send();
  };

  // LAUNCH-PF: "That wasn't an open-market sale", answered before the spend, becomes this step's own explicit answer —
  // `lastSaleDistressed: true`, the card's route — ON THE SAME SALE the notice named and no other
  // (`PurchaseNoticeGate.keepingAnswer`). Once: after it the server's subject carries it, and every call resends it.
  const keptCarried = useRef(false);
  useEffect(() => {
    if (keptCarried.current) return;
    keptCarried.current = true;
    if (held.current.lastSaleDistressed === true) return;
    if (keepsNotOpenMarket(carried.current, first.body.subject?.lastSale ?? null)) correct("lastSaleDistressed", true);
    // `first` is this step's own first answer and `correct` lays over the held set: run once, on mount.
  }, []);

  /** One answer about the record, laid over the others (DT-29, DT-30). */
  function answer(patch: Parameters<typeof withAnswers>[1]): void {
    heldAnswers.current = withAnswers(heldAnswers.current, patch);
    send();
  }

  const subject = response.body.subject;
  const decisions = response.decisions;
  const standing = subject === null ? null : standingOf(subject);
  // THE CARDS SAY WHAT THESE DECISIONS SAY, in the app's words — so their server sentences are not said twice at the top.
  // AND WHILE A RECORD WAITS FOR THE OWNER'S ANSWER, NO GAP IS STATED: every field is empty because nothing has been
  // adopted yet, not because the county lacks it — "stating a data gap we haven't established is worse than staying
  // quiet" (SubjectPropertyView.swift:427-439). The requirement sentence under Continue still says what is missing.
  const awaiting = subject !== null && standingOf(subject)?.standing === "awaitingOwner";
  const cardsSpeak = new Set<string>([
    ...(standing !== null ? ["I-2", "I-4/I-5", "NEW-1", "NEW-7"] : []),
    ...(awaiting ? ["G-4", "NEW-3"] : []),
  ]);
  const blocks = blockingDecisions(decisions);
  const stops = blocks.filter((b) => !RECORD_REFUSALS.has(b.id));
  const new7 = decisions["NEW-7"];
  const new7Blocks = ranBlock(new7);

  /** SubjectPropertyView.swift:675-721, in its order. The owner's edits reach the server before anything is judged. */
  async function attemptContinue(skipValue: boolean): Promise<void> {
    await chain.current;
    if (lastFailed.current) return;
    const now = latest.current;
    // COMMITTING HERE (SubjectPropertyView.swift:679): pressing Continue is the owner saying the parcel is finished.
    if ((now.body.subject?.parcel?.value ?? "").trim() !== "") setParcelCommitted(true);
    const d = now.decisions;
    if (ranBlock(d["P-1"])) return setAlert("p1");
    if (ranWarn(d["NEW-2"]) && !skipValue) return setAlert("new2");
    if (ranBlock(d["NEW-3"])) return setAlert("new3");
    if (ranBlock(d["NEW-7"]) && !groundsChosen) return setAlert("new7");
    // Anything else the server says holds the owner here is said, in its own words, at the top of the page.
    const handled = new Set(["P-1", "NEW-3", "NEW-7"]);
    const others = blockingDecisions(d).filter((b) => !RECORD_REFUSALS.has(b.id) && !handled.has(b.id));
    if (others.length > 0) {
      notes.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      return;
    }
    navigate("/condition");
  }

  function focusField(input: RefObject<HTMLInputElement | null>): void {
    setAlert(null);
    // After the alert has closed and given focus back, so the field keeps it.
    window.setTimeout(() => {
      input.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      input.current?.focus();
    }, 0);
  }

  if (subject === null) {
    // `record` always answers with a subject; one without is not a step the owner can confirm.
    return (
      <>
        <Hero />
        <div className="notrun" data-not-run="notYetComputed">
          <HeldLine id="check.notRunYet" />
        </div>
      </>
    );
  }

  const ownerEntered = ownerEnteredOf(subject, decisions);
  const requirement = requirementSentence(subject, decisions["G-4"], ownerEntered);
  const assessedMissing = subject.assessedValue === null;
  const reason = standing?.reason ?? null;
  const showRefusal = standing?.standing === "notAdopted" && reason === "differentProperty" && !refusalDismissed;
  // `ownerSuppliedDetailsCard` shows once the facts are the owner's (:1207): not while the refusal card still asks, and not
  // after "No, use what I entered" — the app leaves its facts' origin alone there (`rejectPendingMismatch`, :2917-2920).
  const showOwnerCard = standing !== null && standing.standing === "notAdopted" && reason !== "ownerDeclined" && !showRefusal;
  const partial = partialRecords(subject);
  const p1 = decisions["P-1"];
  const assessed = subject.assessedValue?.value ?? null;
  const new2 = decisions["NEW-2"];
  const lowValue = ranBlock(p1) || ranWarn(new2)
    ? { ...lowValueLine(diagnosisOf(p1), assessed), code: codeOf(p1) ?? (new2.ran && new2.outcome === "warn" ? new2.copy.code : undefined) }
    : null;
  const prompt = p1Prompt(diagnosisOf(p1), assessed);
  const today = todayIso();

  return (
    <div className="details-step">
      <Hero />

      {/* THE IMAGE AT THE TOP, where a wide screen has room for it. Only where the place came from a record the owner's
          property was matched to (SubjectPropertyView.swift:1044-1046): on the owner-entered path the pin is the
          geocoder's, which can land on the house next door. */}
      {!ownerEntered && subject.coordinates != null && <SatelliteImage at={subject.coordinates} />}

      <div ref={notes}>
        {blocks.length > 0 && (
          <div data-record-blocks={blocks.map((b) => b.id).join(" ")} data-record-stops={stops.map((b) => b.id).join(" ")}>
            {blocks
              .filter((b) => !SHOWN_BELOW.has(b.id) && !cardsSpeak.has(b.id))
              .map((b) => (b.copy === null ? null : <CopyNote key={b.id} copy={b.copy} tone="stop" />))}
          </div>
        )}
        {/* A check that has not run is said here. One that ran is said beside the assessed value, in the app's words
            (DT-33); an assessed value not given yet is said by the requirement sentence below (F-WP1B-5). */}
        {!p1.ran && !assessedMissing && <DecisionView decision={p1} what="Whether the assessed value is plausible" />}
        {!decisions["NEW-2"].ran && !assessedMissing && (
          <DecisionView decision={decisions["NEW-2"]} what="Whether the assessed value looks too low" />
        )}
        {!awaiting && <DecisionView decision={decisions["NEW-3"]} what="Whether we have the finished square footage" />}
        {standing === null && <DecisionView decision={decisions["I-2"]} what="Whether the record matches your address" />}
      </div>

      {/* ── the app's cards, in its order (SubjectPropertyView.swift:477-484) ── */}
      {standing?.standing === "awaitingOwner" && (
        <RecordQuestionCard
          standing={standing}
          unit={subject.unit}
          onAnswer={(yes) => answer({ confirmations: { record: yes } })}
          onPick={(c) => setPendingPick(c)}
        />
      )}
      {showRefusal && (
        <RefusalCard
          explanation={refusalExplanation(decisions["I-2"], subject.address)}
          code={codeOf(decisions["I-2"]) ?? codeOf(decisions["I-4/I-5"])}
          onEnter={() => setRefusalDismissed(true)}
        />
      )}
      {reason === "wholeBuilding" && <BuildingCard code={codeOf(decisions["NEW-1"])} />}
      {showOwnerCard && (
        <OwnerSuppliedCard unreachable={reason === "recordsUnavailable"} parcelEmpty={(subject.parcel?.value ?? "").trim() === ""} />
      )}
      {new7.ran && new7.outcome === "block" && (
        <PurchaseCard
          new7={new7}
          sale={subject.lastSale}
          risk={response.body.canRaise.risk}
          groundsChosen={groundsChosen}
          onGrounds={() => setGroundsChosen(true)}
          correct={correct}
          cardRef={purchaseCard}
        />
      )}
      {partial !== null && <PartialRecordsNote titleId={partial.titleId} bodyId={partial.bodyId} body={partial.body} />}

      <div className="details-grid">
        <div className="details-col">
          <IdentificationCard
            subject={subject}
            standing={standing}
            ownerEntered={ownerEntered}
            parcelCommitted={parcelCommitted}
            onParcel={(typed) => commitText("parcel", typed, subject.parcel, correct)}
            onWrongParcel={() => answer({ unitChoice: undefined })}
          />
          <ValueCard
            subject={subject}
            inputRef={valueInput}
            lowValue={lowValue}
            onAssessed={(typed) => commitAssessed(typed, subject, correct)}
            onTaxYear={(year) => correct("taxYear", year)}
          />
          <RatioCard
            v5={decisions["V-5"]}
            inForce={response.body.ratio}
            check={subject.ratioCheck ?? null}
            state={subject.state}
            today={today}
            correct={correct}
          />
        </div>
        <div className="details-col">
          <CharacteristicsCard subject={subject} sqftRef={sqftInput} correct={correct} />
          <JurisdictionCard subject={subject} deadline={response.body.jurisdiction.deadline} today={today} correct={correct} />
        </div>
      </div>

      {failure !== null && <Refusal failure={failure} context="Your correction" />}
      <DegradedList degraded={degraded} />

      <div className="step-footer">
        {requirement !== null && (
          <p className="requirement" data-held-copy={requirement.id} data-missing={requirement.missing}>
            {requirement.text}
          </p>
        )}
        <button
          className="btn primary"
          type="button"
          disabled={requirement !== null}
          data-route={groundsChosen && new7Blocks ? "grounds" : undefined}
          data-held-copy="details.continue"
          onClick={() => void attemptContinue(valueSkipped)}
        >
          {words("details.continue")}
        </button>
      </div>

      {alert === "p1" && p1.ran && p1.outcome === "block" && (
        <AppAlert
          kind="P-1"
          title={words(prompt.titleId)}
          titleId={prompt.titleId}
          buttons={[
            { label: words("details.alert.p1.enter"), wordsId: "details.alert.p1.enter", onPress: () => focusField(valueInput) },
            { label: words("details.alert.cancel"), wordsId: "details.alert.cancel", onPress: () => setAlert(null), cancel: true },
          ]}
        >
          {/* The app's message for the cause the server diagnosed (`valueBlockMessage`, :396-405; F-WP1C-4). */}
          <p data-held-copy={prompt.messageId}>{prompt.message}</p>
        </AppAlert>
      )}
      {alert === "new2" && (
        <AppAlert
          kind="NEW-2"
          title={words("details.alert.new2.title")}
          titleId="details.alert.new2.title"
          buttons={[
            { label: words("details.alert.new2.edit"), wordsId: "details.alert.new2.edit", onPress: () => focusField(valueInput) },
            {
              label: words("details.alert.new2.correct"),
              wordsId: "details.alert.new2.correct",
              cancel: true,
              onPress: () => {
                setAlert(null);
                setValueSkipped(true);
                void attemptContinue(true);
              },
            },
          ]}
        >
          <Words
            id="details.alert.new2.message"
            as="p"
            values={{ value: subject.assessedValue === null ? "" : money(subject.assessedValue.value) }}
          />
        </AppAlert>
      )}
      {alert === "new3" && (
        <AppAlert
          kind="NEW-3"
          title={words("details.alert.new3.title")}
          titleId="details.alert.new3.title"
          buttons={[{ label: words("details.alert.new3.add"), wordsId: "details.alert.new3.add", onPress: () => focusField(sqftInput) }]}
        >
          <Words id="details.alert.new3.message" as="p" className="pre-line" />
        </AppAlert>
      )}
      {alert === "new7" && (
        <AppAlert
          kind="NEW-7"
          title={words("details.alert.new7.title")}
          titleId="details.alert.new7.title"
          buttons={[
            {
              label: words("details.alert.new7.show"),
              wordsId: "details.alert.new7.show",
              onPress: () => {
                setAlert(null);
                window.setTimeout(() => purchaseCard.current?.scrollIntoView?.({ behavior: "smooth", block: "center" }), 0);
              },
            },
          ]}
        >
          <Words id="details.alert.new7.message" as="p" />
        </AppAlert>
      )}
      {pendingPick !== null && (
        <AppAlert
          kind="unit-pick"
          title={words("details.unit.confirm.title")}
          titleId="details.unit.confirm.title"
          buttons={[
            {
              label: words("details.unit.confirm.use"),
              wordsId: "details.unit.confirm.use",
              onPress: () => {
                const pick = pendingPick;
                setPendingPick(null);
                answer({ unitChoice: pick.candidateId });
              },
            },
            { label: words("details.alert.cancel"), wordsId: "details.alert.cancel", onPress: () => setPendingPick(null), cancel: true },
          ]}
        >
          <p className="pre-line" data-held-copy="details.unit.confirm.message">
            {unitConfirmation(pendingPick)}
          </p>
        </AppAlert>
      )}
    </div>
  );
}

// ── the cards ─────────────────────────────────────────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <header className="step-hero">
      <span className="hero-disc" aria-hidden="true">
        <svg width="24" height="24" viewBox="0 0 24 24" focusable="false">
          <path d="M12 3.2 2.8 11a1 1 0 0 0 1.3 1.5l.9-.8V20a1 1 0 0 0 1 1h4.2v-5.4h3.6V21H18a1 1 0 0 0 1-1v-8.3l.9.8a1 1 0 0 0 1.3-1.5Z" fill="currentColor" />
        </svg>
      </span>
      <Words id="details.hero.title" as="h1" />
      <Words id="details.hero.body" as="p" className="sub" />
    </header>
  );
}

function IdentificationCard({
  subject,
  standing,
  ownerEntered,
  parcelCommitted,
  onParcel,
  onWrongParcel,
}: {
  subject: SubjectView;
  standing: Standing | null;
  ownerEntered: boolean;
  parcelCommitted: boolean;
  onParcel: (typed: string) => void;
  onWrongParcel: () => void;
}) {
  const parcel = subject.parcel;
  // PROVENANCE + COMMITMENT, never "is it non-empty" (SubjectPropertyView.swift:1679-1706, Models.swift:986-990): a
  // parcel the record supplied is locked at once; one the owner typed stays editable for the whole step and locks when
  // they press Continue.
  const fromRecord = parcel !== null && (parcel.source === "provider" || parcel.source === "county");
  const locked = parcel !== null && parcel.value.trim() !== "" && (fromRecord || parcelCommitted);
  const unit = (subject.unit ?? "").trim();
  // "Wrong parcel? Choose a different unit" (:1586-1605): a mis-tap is not a property switch. Here it is offered where the
  // owner PICKED a parcel from the building's list — letting go of the pick puts the list back, with no new lookup, because
  // the server holds it; with no pick there is no list to go back to.
  const picked = standing !== null && standing.standing === "adopted" && standing.unitChoice !== null;
  return (
    <BrandCard icon="pin" titleId="details.identification.title" data-card="identification">
      <LockedRow labelId="details.address.label" value={subject.address.trim() === "" ? "—" : subject.address} />
      {unit !== "" && <LockedRow labelId="details.unit.label" value={unit} />}
      {unit !== "" && picked && (
        <button type="button" className="link wrong-parcel" data-route-choice="wrong-parcel" onClick={onWrongParcel}>
          <Words id="details.unit.wrong" as="span" />
        </button>
      )}
      {locked ? (
        <LockedRow labelId="details.parcel.label" value={parcel.value} field="parcel" fact={parcel} />
      ) : (
        <TextField
          labelId="details.parcel.label"
          value={parcel?.value ?? ""}
          placeholderId={ownerEntered ? "details.parcel.placeholder.required" : "details.parcel.placeholder.optional"}
          onCommit={onParcel}
          field="parcel"
          fact={parcel}
        />
      )}
    </BrandCard>
  );
}

function ValueCard({
  subject,
  inputRef,
  lowValue,
  onAssessed,
  onTaxYear,
}: {
  subject: SubjectView;
  inputRef: RefObject<HTMLInputElement | null>;
  lowValue: { id: DetailsWordingId; text: string; code: string | undefined } | null;
  onAssessed: (typed: number | null) => void;
  onTaxYear: (year: number) => void;
}) {
  const year = subject.assessedYear?.value ?? null;
  const thisYear = new Date().getFullYear();
  const taxYear = subject.taxYear ?? thisYear;
  return (
    <BrandCard icon="dollar" titleId="details.value.title" data-card="value">
      <NumberField
        labelId="details.value.assessed"
        value={subject.assessedValue?.value.amount ?? null}
        prefix="$"
        whole
        inputRef={inputRef}
        onCommit={onAssessed}
        field="assessedValue"
        fact={subject.assessedValue}
      />
      {/* The record's own vintage, kept when the owner corrects the figure: it dates the value "on file" (:1814). */}
      {year !== null && <Words id="details.value.year" as="p" className="caption warn" values={{ year: String(year) }} />}
      <Words id="details.value.helper" as="p" className="caption" />
      {/* THE LOW FIGURE, SAID WHERE IT IS (DT-33, :1860-1866): P-1 blocked or NEW-2 warned, and the server's diagnosis
          picks the app's line for why. */}
      {lowValue !== null && (
        <p className="caption low-value" data-held-copy={lowValue.id} data-low-value="true" data-copy-code={lowValue.code}>
          {lowValue.text}
        </p>
      )}
      <YearStepper
        labelId="details.taxYear.label"
        helperId="details.taxYear.helper"
        value={taxYear}
        min={thisYear - 5}
        max={thisYear + 1}
        onCommit={onTaxYear}
      />
    </BrandCard>
  );
}

const TYPE_OPTIONS: readonly { code: PropertyTypeCode; label: DetailsWordingId }[] = [
  // SubjectPropertyView.swift:3105, in its order, each on the contract's code. "Twin / Semi-detached" and
  // "Multi-family" are the app's labels for the codes "Twin/Semi-detached" and "Multi-unit" (DT-17).
  { code: "Single-family", label: "details.type.singleFamily" },
  { code: "Twin/Semi-detached", label: "details.type.twin" },
  { code: "Townhouse", label: "details.type.townhouse" },
  { code: "Condo", label: "details.type.condo" },
  { code: "Multi-unit", label: "details.type.multiFamily" },
  { code: "Manufactured", label: "details.type.manufactured" },
  { code: "Other", label: "details.type.other" },
];

function CharacteristicsCard({
  subject,
  sqftRef,
  correct,
}: {
  subject: SubjectView;
  sqftRef: RefObject<HTMLInputElement | null>;
  correct: Correct;
}) {
  const type = subject.propertyType?.value ?? null;
  const sqftFix = sqftCorrection(subject.sqft);
  // R-pending-size-label (L1): what the server's size-basis guard said about this size (contract 2.33.0), or nothing —
  // the switch off, or a subject stored without it — and then the app's two sentences exactly as before.
  const sizeWording = subject.sqftWording ?? null;
  const publicRecords = sizeWording?.recordLabel === "publicRecords";
  const gaps = unitGaps(subject);
  return (
    <BrandCard icon="ruler" titleId="details.characteristics.title" footerId="details.characteristics.footer" data-card="characteristics">
      {/* What the county does not carry for the parcel the owner picked, by name (:2295-2308). */}
      {gaps !== null && (
        <p className="caption unit-gaps" data-held-copy="details.unit.gaps">
          {gaps}
        </p>
      )}
      <SelectField
        labelId="details.type.label"
        value={type ?? ""}
        field="propertyType"
        fact={subject.propertyType}
        options={TYPE_OPTIONS.map((o) => ({ value: o.code, label: words(o.label), wordsId: o.label }))}
        onChange={(picked) => {
          if (picked === (type ?? "")) return;
          // "Select" is no correction: the type goes back to the owner's own pick.
          correct("propertyType", picked === "" ? undefined : (picked as PropertyTypeCode));
        }}
      />
      <UnsupportedTypeNotice type={type} />
      <NumberField
        labelId="details.sqft.label"
        value={subject.sqft?.value ?? null}
        unitId="details.sqft.unit"
        inputRef={sqftRef}
        onCommit={(typed) => commitNumber("sqft", typed, subject.sqft, correct)}
        field="sqft"
        fact={subject.sqft}
      />
      {sqftFix !== null &&
        (sqftFix.corrected < sqftFix.record ? (
          sizeWording?.note === "basementMatch" ? (
            // Tyler's sentence 1, in place of "We'll cite this as a factual record error": the server found the record's
            // size probably includes a finished basement and this figure is the county's above-grade area.
            <SizeWords
              id="sizeBasis.basementMatch"
              className="caption good"
              values={{ record: Math.trunc(sqftFix.record), corrected: Math.trunc(sqftFix.corrected) }}
            />
          ) : (
            <Words
              id="details.sqft.favorable"
              as="p"
              className="caption good"
              values={{ record: Math.trunc(sqftFix.record), corrected: Math.trunc(sqftFix.corrected) }}
              publicRecords={publicRecords}
            />
          )
        ) : (
          <Words
            id="details.sqft.unfavorable"
            as="p"
            className="caption warn"
            values={{ record: Math.trunc(sqftFix.record), corrected: Math.trunc(sqftFix.corrected) }}
            publicRecords={publicRecords}
          />
        ))}
      {/* Tyler's sentence 2: the record's size probably includes a finished basement and the owner has not changed it. */}
      {sizeWording?.note === "basementCaption" && subject.sqft !== null && subject.sqft.source !== "ownerCorrected" && (
        <SizeWords id="sizeBasis.basementCaption" className="caption" values={{ record: Math.trunc(subject.sqft.value) }} />
      )}
      <SelectField
        labelId="details.beds.label"
        value={subject.beds === null ? "" : String(subject.beds.value)}
        field="beds"
        fact={subject.beds}
        options={bedroomOptions(subject.beds?.value ?? null).map((n) => ({ value: String(n), label: bedroomLabel(n) }))}
        onChange={(picked) => commitNumber("beds", picked === "" ? null : Number(picked), subject.beds, correct)}
      />
      <SelectField
        labelId="details.baths.label"
        value={subject.baths === null ? "" : String(subject.baths.value)}
        field="baths"
        fact={subject.baths}
        options={bathroomOptions(subject.baths?.value ?? null).map((n) => ({ value: String(n), label: bathroomLabel(n) }))}
        onChange={(picked) => commitNumber("baths", picked === "" ? null : Number(picked), subject.baths, correct)}
      />
      {/* A CONDO UNIT HAS NO LOT (SubjectPropertyView.swift:2398-2408; Models.swift:562-565). */}
      {type !== "Condo" && type !== "Multi-unit" && (
        <NumberField
          labelId="details.lot.label"
          value={subject.lotSizeSqft?.value ?? null}
          unitId="details.sqft.unit"
          onCommit={(typed) => commitNumber("lotSizeSqft", typed, subject.lotSizeSqft ?? null, correct)}
          field="lotSizeSqft"
          fact={subject.lotSizeSqft}
        />
      )}
      <NumberField
        labelId="details.year.label"
        value={subject.yearBuilt?.value ?? null}
        placeholderId="details.year.placeholder"
        whole
        plain
        onCommit={(typed) => commitNumber("yearBuilt", typed, subject.yearBuilt, correct)}
        field="yearBuilt"
        fact={subject.yearBuilt}
      />
    </BrandCard>
  );
}

/**
 * The app's notice under the picker (SubjectPropertyView.swift:2317-2334) — for a manufactured home and a multi-unit
 * building. NOT for "Other": the app shows the rental building's notice there, which is the defect D9 lists and this
 * does not copy; the app has no words for "Other" of its own.
 */
function UnsupportedTypeNotice({ type }: { type: PropertyTypeCode | null }) {
  if (type !== "Manufactured" && type !== "Multi-unit") return null;
  const manufactured = type === "Manufactured";
  return (
    <div className="type-notice" data-type-notice={manufactured ? "manufactured" : "rental"}>
      <Words
        id={manufactured ? "details.type.unsupported.manufactured.title" : "details.type.unsupported.rental.title"}
        as="b"
      />
      <Words
        id={manufactured ? "details.type.unsupported.manufactured.body" : "details.type.unsupported.rental.body"}
        as="p"
        className="caption"
      />
    </div>
  );
}
